import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import type { PagedListReturn } from '@invara-dev/react-base';
import { useFrontierAuth } from 'src/util/auth/useFrontierAuth';
import { projectDashboardNavQueryKeys } from 'src/features/companyProjects/navigation/api/projectDashboardNavQueryKeys';
import { projectContainerQueryKeys } from 'src/features/companyProjects/projectContainers/api/projectContainerQueryKeys';
import { projectContainerCompanyKeys } from 'src/features/companyProjects/projectContainers/dashboard/sections/company/api/projectContainerCompanyKeys';
import type { ProjectContainerTableModel } from 'src/features/companyProjects/projectContainers/manageContainers/types/ProjectContainerTableModel';
import { normalizeInboxEvent } from 'src/features/companyProjects/projectIntake/inbox/hubs/ProjectIntakeInboxHubActions';
import { projectIntakeQueryKeys } from 'src/features/companyProjects/projectIntake/api/projectIntakeQueryKeys';
import { ProjectIntakeEventType } from 'src/features/companyProjects/projectIntake/inbox/types';
import { projectHotelQueryKeys } from 'src/features/companyProjects/projectContainers/dashboard/sections/hotels/api/projectHotelQueryKeys';
import { projectBookingKeys } from 'src/features/companyProjects/projectContainers/dashboard/sections/bookings/api/projectBookingKeys';
import {
  applyProjectContainerListEventToInfiniteTablePages,
  normalizeContainerEvent,
  normalizeFolderEvent,
  normalizeProjectAssessmentEvent,
  normalizeProjectBookingEvent,
  normalizeProjectHotelEvent,
  normalizeProjectContainerListEvent,
  ProjectFolderEventType,
  type ProjectContainerListEventDto
} from 'src/features/companyProjects/realtime/events/projectContainerEvents';
import { subscribeProjectDashboard } from 'src/features/companyProjects/realtime/dashboard/projectDashboardHub';
import {
  shouldInvalidateAssignedContainersForContainerEvent,
  shouldInvalidateAssignedContainersForInboxEvent,
  useProjectDashboardRealtimeStore
} from 'src/features/companyProjects/realtime/stores/projectDashboardRealtimeStore';

/** Collect dashboard invalidations so a burst of hub events refreshes each query key once. */
const dashboardQueryInvalidationWindowMs = 50;

/**
 * Single subscription point: SignalR → Zustand store + React Query invalidations / manage-projects table patches.
 */
export const ProjectDashboardRealtimeBridge = () => {
  const queryClient = useQueryClient();
  const { user, hasLoaded, isAuthenticated } = useFrontierAuth();

  useEffect(() => {
    if (!hasLoaded || !isAuthenticated) {
      return;
    }

    const pendingInvalidations = new Map<string, QueryKey>();
    let invalidationTimer: ReturnType<typeof setTimeout> | null = null;

    const flushScheduledInvalidations = () => {
      invalidationTimer = null;
      const queryKeys = [...pendingInvalidations.values()];
      pendingInvalidations.clear();
      for (const queryKey of queryKeys) {
        void queryClient.invalidateQueries({ queryKey });
      }
    };

    const scheduleInvalidation = (queryKey: QueryKey) => {
      pendingInvalidations.set(JSON.stringify(queryKey), queryKey);
      if (invalidationTimer != null) return;
      invalidationTimer = setTimeout(flushScheduledInvalidations, dashboardQueryInvalidationWindowMs);
    };

    const clearScheduledInvalidations = () => {
      if (invalidationTimer != null) {
        clearTimeout(invalidationTimer);
        invalidationTimer = null;
      }
      pendingInvalidations.clear();
    };

    const unsubscribe = subscribeProjectDashboard({
      OnInboxEvent: (...rawArgs: unknown[]) => {
        try {
          if (useProjectDashboardRealtimeStore.getState().isProjectIntakePaused) {
            return;
          }
          const event = normalizeInboxEvent(rawArgs);
          if (!event) return;
          useProjectDashboardRealtimeStore.getState().applyInboxEvent(event);

          if (shouldInvalidateAssignedContainersForInboxEvent(event)) {
            void queryClient.refetchQueries({
              queryKey: projectDashboardNavQueryKeys.myAssignedProjectContainers(),
              type: 'all'
            });
            void queryClient.refetchQueries({
              queryKey: projectDashboardNavQueryKeys.unreadNotAssignedProjectContainers(),
              type: 'all'
            });
          }
          if (event.intakeId) {
            void queryClient.invalidateQueries({ queryKey: projectIntakeQueryKeys.detail(event.intakeId) });
          }
          if (
            event.type === ProjectIntakeEventType.Read ||
            event.type === ProjectIntakeEventType.Unread ||
            event.type === ProjectIntakeEventType.Updated ||
            event.type === ProjectIntakeEventType.Removed ||
            event.type === ProjectIntakeEventType.Merged
          ) {
            scheduleInvalidation(projectContainerQueryKeys.homeFeedRoot());
            scheduleInvalidation(['projectContainerOverview']);
            scheduleInvalidation(projectContainerQueryKeys.folderIntakesRoot());
            scheduleInvalidation(['projectContainerIntakes']);
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      },
      OnContainerEvent: (...args: unknown[]) => {
        const event = normalizeContainerEvent(args);
        if (!event) return;
        useProjectDashboardRealtimeStore.getState().applyContainerEvent(event);
        scheduleInvalidation(projectContainerQueryKeys.homeFeed(event.containerId));
        scheduleInvalidation(['projectContainerOverview', event.containerId]);
        scheduleInvalidation(projectContainerQueryKeys.containerIntakesRoot(event.containerId));
        if (event.fromFolderId != null) {
          scheduleInvalidation(projectContainerQueryKeys.folderIntakes(event.fromFolderId));
        }
        if (event.toFolderId != null) {
          scheduleInvalidation(projectContainerQueryKeys.folderIntakes(event.toFolderId));
        }
        if (shouldInvalidateAssignedContainersForContainerEvent(event)) {
          void queryClient.refetchQueries({
            queryKey: projectDashboardNavQueryKeys.myAssignedProjectContainers(),
            type: 'all'
          });
          void queryClient.refetchQueries({
            queryKey: projectDashboardNavQueryKeys.unreadNotAssignedProjectContainers(),
            type: 'all'
          });
        }
      },
      OnFolderEvent: (...args: unknown[]) => {
        const event = normalizeFolderEvent(args);
        if (!event) return;
        useProjectDashboardRealtimeStore.getState().applyFolderEvent(event);
        if (event.type === ProjectFolderEventType.FolderCreated) {
          void queryClient.invalidateQueries({
            queryKey: ['projectContainerOverview', event.containerId]
          });
        }
      },
      OnProjectHotelsEvent: (...args: unknown[]) => {
        const event = normalizeProjectHotelEvent(args);
        if (!event) return;

        void queryClient.invalidateQueries({ queryKey: projectHotelQueryKeys.list(event.containerId) });
      },
      OnProjectBookingsEvent: (...args: unknown[]) => {
        const event = normalizeProjectBookingEvent(args);
        if (!event) return;

        void queryClient.invalidateQueries({ queryKey: projectBookingKeys.all() });
        void queryClient.invalidateQueries({ queryKey: projectHotelQueryKeys.list(event.containerId) });
      },
      OnProjectContainerEvent: (...args: unknown[]) => {
        const [dto] = args as [ProjectContainerListEventDto];
        const event = normalizeProjectContainerListEvent(dto);
        if (!event) return;

        const currentUserId = user?.UserId;
        useProjectDashboardRealtimeStore.getState().applyProjectContainerListEvent(event, currentUserId);

        const tableQueries = queryClient.getQueryCache().findAll({ queryKey: projectContainerQueryKeys.table() });
        for (const query of tableQueries) {
          const key = query.queryKey as readonly unknown[];
          queryClient.setQueryData<InfiniteData<PagedListReturn<ProjectContainerTableModel>> | undefined>(
            key,
            (current) => applyProjectContainerListEventToInfiniteTablePages(current, event)
          );
        }

        void queryClient.invalidateQueries({
          queryKey: projectContainerQueryKeys.edit(event.containerId),
          type: 'all'
        });
        void queryClient.invalidateQueries({
          queryKey: projectContainerCompanyKeys.overview(event.containerId),
          type: 'all'
        });
        void queryClient.refetchQueries({
          queryKey: projectDashboardNavQueryKeys.unreadNotAssignedProjectContainers(),
          type: 'all'
        });
      },
      OnProjectAssessmentEvent: (...args: unknown[]) => {
        const event = normalizeProjectAssessmentEvent(args);
        if (!event) return;

        void queryClient.invalidateQueries({
          queryKey: projectContainerQueryKeys.assessment(event.containerId),
          type: 'all'
        });
        void queryClient.invalidateQueries({
          queryKey: projectContainerQueryKeys.edit(event.containerId),
          type: 'all'
        });
        void queryClient.invalidateQueries({
          queryKey: projectContainerCompanyKeys.overview(event.containerId),
          type: 'all'
        });
        void queryClient.invalidateQueries({
          queryKey: projectContainerCompanyKeys.contacts(event.containerId),
          type: 'all'
        });
        void queryClient.refetchQueries({
          queryKey: projectDashboardNavQueryKeys.unreadNotAssignedProjectContainers(),
          type: 'all'
        });
      }
    });

    return () => {
      clearScheduledInvalidations();
      unsubscribe();
    };
  }, [hasLoaded, isAuthenticated, queryClient, user?.UserId]);

  return null;
};
